<div class="container-fluid">
  <div class="row">
    <div class="col-12">
      <h2>Zugriffsprotokolle</h2>
      <p class="text-muted">
        Übersicht über alle Zugriffe auf Berichte. Die in UTC gespeicherten
        Zeitpunkte werden in österreichischer Lokalzeit (CET/CEST) angezeigt.
      </p>

      <!-- Error State -->
      @if (error && !loading) {
        <div class="alert alert-danger" role="alert">
          <h4 class="alert-heading">Fehler!</h4>
          <p>{{ error }}</p>
          <hr />
          <button mat-raised-button color="primary" (click)="refreshLogs()">
            <mat-icon>refresh</mat-icon> Erneut versuchen
          </button>
        </div>
      }

      <!-- Content -->
      @if (!error) {
        <div class="mat-elevation-z8">
          <!-- Action Buttons Bar -->
          <div class="p-3 bg-light border-bottom">
            <div class="d-flex justify-content-end">
              <div class="d-flex gap-2">
                <button
                  mat-stroked-button
                  color="primary"
                  (click)="refreshLogs()"
                  [disabled]="loading"
                  >
                  <mat-icon>refresh</mat-icon> Aktualisieren
                </button>
                <button
                  mat-raised-button
                  color="primary"
                  (click)="exportLogs()"
                  [disabled]="dataSource.data.length === 0 || loading"
                  >
                  @if (!loading) {
                    <mat-icon>download</mat-icon>
                  }
                  @if (loading) {
                    <mat-spinner diameter="20"></mat-spinner>
                  }
                  {{ loading ? "Exportiere..." : "Exportieren" }}
                </button>
              </div>
            </div>
          </div>
          <!-- Table Header with Search and Count -->
          <div class="table-header p-3 border-bottom">
            <div class="d-flex justify-content-between align-items-center">
              <mat-form-field appearance="outline" class="search-field">
                <mat-label>Benutzer durchsuchen...</mat-label>
                <input
                  matInput
                  (keyup)="applyFilter($event)"
                  placeholder="Nach Benutzer durchsuchen..."
                  #input
                  />
                <mat-icon matSuffix>search</mat-icon>
              </mat-form-field>
            </div>
          </div>
          <!-- Table Container with Loading State -->
          <div class="table-container">
            <!-- Loading State for Table -->
            @if (loading) {
              <div class="text-center py-5">
                <mat-spinner diameter="50"></mat-spinner>
                <p class="mt-3">Lade Zugriffsprotokolle...</p>
              </div>
            }
            <!-- Table Content -->
            @if (!loading) {
              <table
                mat-table
                [dataSource]="dataSource"
                class="w-100"
                >
                <!-- Date/Time Column -->
                <ng-container matColumnDef="access_datetime">
                  <th mat-header-cell *matHeaderCellDef>
                    Datum/Zeit (CET/CEST)
                  </th>
                  <td mat-cell *matCellDef="let log">
                    {{ log.access_datetime | cetDate }}
                  </td>
                </ng-container>
                <!-- User Column -->
                <ng-container matColumnDef="user">
                  <th mat-header-cell *matHeaderCellDef>Benutzer</th>
                  <td mat-cell *matCellDef="let log">
                    <div>
                      <strong>{{ getDisplayName(log) }}</strong>
                      <br />
                      @if (log.user_email) {
                        <small class="text-muted">{{
                          log.user_email
                        }}</small>
                      }
                    </div>
                  </td>
                </ng-container>
                <!-- Report Column -->
                <ng-container matColumnDef="report">
                  <th mat-header-cell *matHeaderCellDef>Bericht</th>
                  <td mat-cell *matCellDef="let log">
                    <div>
                      <strong>{{
                        log.report_name || "Unbekannter Bericht"
                      }}</strong>
                      <br />
                      @if (log.report_id) {
                        <small class="text-muted"
                          >ID: {{ log.report_id }}</small
                          >
                        }
                      </div>
                    </td>
                  </ng-container>
                  <!-- Project Column -->
                  <ng-container matColumnDef="project">
                    <th mat-header-cell *matHeaderCellDef>Projekt</th>
                    <td mat-cell *matCellDef="let log">
                      {{ log.project_name || "-" }}
                    </td>
                  </ng-container>
                  <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                  <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
                  <!-- No Data Row -->
                  <tr class="no-data-row" *matNoDataRow>
                    <td class="no-data-cell text-center py-4" [attr.colspan]="4">
                      <mat-icon class="mb-2" style="font-size: 48px; opacity: 0.5"
                        >history</mat-icon
                        >
                        <br />
                        @if (dataSource.filter) {
                          <span
                            >Keine Ergebnisse für "{{ dataSource.filter }}" auf dieser
                            Seite</span
                            >
                          }
                          @if (!dataSource.filter && totalItems === 0) {
                            <span
                              >Keine Zugriffsprotokolle verfügbar</span
                              >
                            }
                            @if (!dataSource.filter && totalItems > 0) {
                              <span
                                >Keine Einträge auf dieser Seite</span
                                >
                              }
                            </td>
                          </tr>
                        </table>
                      }
                    </div>
                    <!-- Paginator -->
                    <div
                      class="flex items-center justify-end px-6 py-4 bg-white border-t border-gray-200"
                      >
                      <!-- Pagination info and controls -->
                      <div class="flex items-center space-x-4">
                        <!-- Page info -->
                        <span class="text-sm text-gray-600 font-medium">
                          {{ currentPage * pageSize + 1 }} -
                          {{ Math.min((currentPage + 1) * pageSize, totalItems) }} von
                          {{ totalItems }}
                        </span>
                        <!-- Navigation buttons -->
                        <div class="flex items-center space-x-1">
                          <button
                            mat-icon-button
                            [disabled]="currentPage === 0"
                            (click)="goToFirstPage()"
                            class="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Erste Seite"
                            >
                            <mat-icon class="text-lg">first_page</mat-icon>
                          </button>
                          <button
                            mat-icon-button
                            [disabled]="currentPage === 0"
                            (click)="goToPreviousPage()"
                            class="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Vorherige Seite"
                            >
                            <mat-icon class="text-lg">chevron_left</mat-icon>
                          </button>
                          <button
                            mat-icon-button
                            [disabled]="currentPage >= getTotalPages() - 1"
                            (click)="goToNextPage()"
                            class="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Nächste Seite"
                            >
                            <mat-icon class="text-lg">chevron_right</mat-icon>
                          </button>
                          <button
                            mat-icon-button
                            [disabled]="currentPage >= getTotalPages() - 1"
                            (click)="goToLastPage()"
                            class="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Letzte Seite"
                            >
                            <mat-icon class="text-lg">last_page</mat-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
